/**
 * SOLID Review
 * S — Strong. One concern: the first-party cookie modal's choice — the advertising toggle, re-synced with the saved choice when the
 *     modal opens, and what each button saves.
 * O — Good. A new category is one more field of its state and of `categoriesOf`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The toggle, what a choice saves, and the view-model surface.
 * D — Strong. The consent vocabulary; no port — saving is the provider's.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IEntityCookieConsentCategories } from '../../application/ports/ClientVocabulary';

export interface IStateCookiePreferences {
  readonly advertising: boolean;
}

/** What the modal's view hands it: whether it is open, and the saved choice's two advertising signals. */
type TPreferencesInputs = readonly [isOpen: boolean, adStorage: boolean, adPersonalization: boolean];

/**
 * THE COOKIE MODAL'S CHOICE (frontend-framework-oo P3, 2026-10-29, §28.295) — `consent/CookiePreferencesModal.tsx`'s state and
 * effect: the toggle is seeded from the saved choice when the modal mounts, and re-synced each time it opens (and while it is open, if
 * the saved choice changes) — `useState` only seeded on the first mount, and reopening showed a stale toggle until the effect did.
 */
export class CookiePreferencesViewModel implements IServiceViewModel<IStateCookiePreferences> {
  private readonly state: ObservableState<IStateCookiePreferences>;
  private readonly reactions = new Reactions<TPreferencesInputs>([{
    deps: (inputs) => inputs,
    run: ([isOpen, adStorage, adPersonalization]) => {
      if (!isOpen) return;
      const synced: Partial<IStateCookiePreferences> = { advertising: adStorage && adPersonalization };
      this.state.update(synced);
    },
  }]);

  constructor(advertising: boolean) {
    const seeded: IStateCookiePreferences = { advertising };
    this.state = new ObservableState<IStateCookiePreferences>(seeded);
  }

  /**
   * What a choice saves. Advertising groups the three Consent Mode ad signals. Analytics is not implemented yet, so it is hidden from
   * the modal and always denied (re-add its toggle, and pass its value here, when analytics is introduced); functionality is always
   * granted, as strictly necessary.
   */
  static categoriesOf(ads: boolean): IEntityCookieConsentCategories {
    return { adStorage: ads, adUserData: ads, adPersonalization: ads, analyticsStorage: false, functionality: true };
  }

  readonly setAdvertising = (advertising: boolean): void => {
    const toggled: Partial<IStateCookiePreferences> = { advertising };
    this.state.update(toggled);
  };

  getSnapshot(): IStateCookiePreferences {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TPreferencesInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TPreferencesInputs);
  }
}
